import { OrderHistoryDto } from '../types';
import { ProductType } from '@/types/webapi';

/**
 * Merges two order lists while maintaining pagination metadata.
 * @param prevList Previously loaded list of orders
 * @param newList Newly fetched list of orders
 * @param totalMatching Total number of matching orders
 * @param skip Number of items skipped (offset)
 * @param take Number of items to take (limit)
 * @returns An object containing the merged order list and pagination info
 */
export const mergeOrderLists = (
    prevList: OrderHistoryDto[],
    newList: OrderHistoryDto[],
    totalMatching: number,
    skip: number,
    take: number
) => ({
    orders: [...prevList, ...newList],
    totalMatching,
    skip,
    take
});

/**
 * Checks if the maximum number of items has been reached for pagination.
 * @param totalMatching Total number of matching items
 * @param skip Number of skipped items (offset)
 * @param take Number of items taken (limit)
 * @returns True if all items are loaded, false otherwise
 */
export const isMaxItemsReached = (totalMatching: number = 0, skip: number, take: number): boolean => totalMatching <= skip + take;

/**
 * Checks if any recent order contains a pending travel card
 * not found in the user's known card numbers.
 *
 * @param recentOrders List of recent order history entries
 * @param userCardNumbers Array of known user travel card Numbers
 * @returns True if at least one order contains a new (pending) travel card, false otherwise
 */
export const isPendingCardFoundInRecentOrders = (recentOrders: OrderHistoryDto[] | undefined, userCardNumbers: string[]): boolean =>
    recentOrders?.some((order: OrderHistoryDto) => {
        return order?.lineItems
            .filter(lineItem => !isOrderTypeTicket(lineItem.productType))
            .some(lineItem => !userCardNumbers.includes(lineItem.travelCard?.id));
    }) || false;

/**
 * Checks if an order type is a ticket (not a travel card).
 *
 * @param productType type of the product in the order
 * @returns True if at least one order contains a new (pending) travel card, false otherwise
 */
export const isOrderTypeTicket = (productType: number): boolean =>
    productType !== ProductType.TravelCard && productType !== ProductType.TravelMoney;
